// @ts-ignore
import { io, Socket } from 'socket.io-client';

import RealtimeSubject from '../enum/RealtimeSubject';
import { RealtimeOperationPayload } from '../type/RealtimeOperationPayload';

type OperationHandler = (payload: RealtimeOperationPayload) => void;
type ReconnectHandler = (attempts: number) => void;

export interface RealtimeClientOptions {
    host: string
    path: string
    logger: any
}

export class RealtimeClient {

    private options: RealtimeClientOptions
    private logger: any
    private socket: Socket

    private reconnectHandler: ReconnectHandler;

    // @ts-ignore
    private handlers = new Map<RealtimeSubject, OperationHandler>()

    private queue = [];

    constructor(options: RealtimeClientOptions) {

        this.logger = options.logger?.child('Realtime');
        this.options = options;
    }

    connect(token: string){

        if(this.socket){
            this.disconnect();
        }

        const socket = io(this.options.host, {
            path: this.options.path,
            auth: {
                token
            }
        });

        socket.io.on('reconnect', (attempts) =>
        {
            this.logger.info(`Socket reconnected after ${attempts} attempts`);

            if(this.reconnectHandler){
                this.reconnectHandler(attempts);
            }
        });

        socket.on('connect', () => {
            this.logger.info('Socket connected');
        });

        socket.on('connect_error', (error) => {
            this.logger.info('Socket connection failed', error);
        });

        socket.on('disconnect', () => {
            this.logger.info('Socket disconnected');
        });

        socket.on('operation', data => {

            this.logger.info('Received operation', data);

            const { subject } = data;
            if(!subject){
                this.logger.error('Invalid payload received');
                return;
            }

            const handler = this.handlers.get(subject);
            if(!handler){
                this.logger.error('No handler for subject', subject);
                return;
            }

            this.enqueue(() => handler(data));
        });

        this.socket = socket;
    }

    disconnect(){

        if(!this.socket){
            return;
        }

        this.socket.disconnect();
        this.socket = null;
    }

    handler(subject: RealtimeSubject, handler: OperationHandler): this {

        this.handlers.set(subject, handler);
        return this;
    }

    removeHandler(subject){

        this.handlers.delete(subject);
    }

    onReconnect(handler: ReconnectHandler): this {

        this.reconnectHandler = handler;
        return this;
    }

    dequeue(){

        if (!this.queue.length) return

        const handlerResult = this.queue[0]();

        const done = () => {
            this.queue.shift();
            this.dequeue();
        }

        if(handlerResult){
            handlerResult.finally(done);
        }
        else {
            done();
        }
    }

    enqueue(func){

        this.queue.push(func);

        if (this.queue.length === 1){
            this.dequeue()
        }
    }
}